import React, { useState, useEffect, useRef } from 'react';
import { Clock } from '../icons/index';
import { RelationIcon, ICON_STYLES } from '../icons';
import { QuickActionType } from '../../types';
import { getQuickActionConfig } from '../../utils/quickActionConfig';

interface TimerActionButtonsProps {
  currentPhase: any;
  onBreakConversation: () => void;
  onNextPhase: () => Promise<void>;
  onQuickAction: () => void; // Replaces onModifyAppointment
  onScheduleAppointment: () => void;
  onPlusOneMinute: any;
  timer: number;
  formatTime: any;
  isTimerActive: boolean;
  phaseName: string;
  aiEnabled: boolean;
  onToggleAi: any;
  onToggleAiCard?: () => void;
  onStartMeetup: any;
  onEndMeetup: any;
  meetupStarted: boolean;
  quickActionType?: QuickActionType; // Customizable quick action type
  /** When true the "Volgende" button is disabled to prevent double-click race conditions */
  isTransitionPending?: boolean;
}

// Stopwatch component for physical meetings
function PhysicalStopwatch({ phaseName, onNextPhase }: { phaseName: string; onNextPhase: () => Promise<void> }) {
  const [elapsed, setElapsed] = useState(0);
  const [isRunning, setIsRunning] = useState(true); // Auto-start when meeting becomes active
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (isRunning) {
      intervalRef.current = setInterval(() => setElapsed(prev => prev + 1), 1000);
    } else if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [isRunning]);

  const formatStopwatch = (seconds: number) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    if (h > 0) return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  return (
    <div className="w-full">
      <div className="flex items-center gap-2">
        {/* Stopwatch display */}
        <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-xl px-3 py-2 flex-1">
          <div className={`w-2 h-2 rounded-full flex-shrink-0 ${isRunning ? 'bg-green-500 animate-pulse' : elapsed > 0 ? 'bg-amber-500' : 'bg-gray-300'}`} />
          <span className="font-mono font-bold text-lg text-gray-900 tracking-wider">{formatStopwatch(elapsed)}</span>
          <span className="text-xs text-gray-400 truncate">{phaseName}</span>
        </div>

        {/* Start / Pause / Stop controls */}
        <div className="flex gap-1.5 flex-shrink-0">
          {!isRunning ? (
            <button
              onClick={() => setIsRunning(true)}
              className="px-3 py-2 bg-green-500 hover:bg-green-600 text-white text-xs font-semibold rounded-xl transition-all active:scale-95 shadow-sm"
            >
              {elapsed > 0 ? '▶ Hervat' : '▶ Start'}
            </button>
          ) : (
            <button
              onClick={() => setIsRunning(false)}
              className="px-3 py-2 bg-amber-500 hover:bg-amber-600 text-white text-xs font-semibold rounded-xl transition-all active:scale-95 shadow-sm"
            >
              ⏸ Pauze
            </button>
          )}
          {elapsed > 0 && (
            <button
              onClick={() => {
                setIsRunning(false);
                onNextPhase();
              }}
              className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold rounded-xl transition-all active:scale-95 shadow-sm"
            >
              ✓ Afronden
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function TimerActionButtons({
  timer,
  formatTime,
  phaseName,
  onPlusOneMinute,
  onBreakConversation,
  onNextPhase,
  onQuickAction,
  onScheduleAppointment,
  currentPhase,
  aiEnabled,
  onToggleAiCard,
  quickActionType = 'agenda',
  isTransitionPending = false,
}: TimerActionButtonsProps) {
  const quickActionConfig = getQuickActionConfig(quickActionType);

  // Physical meeting: show stopwatch only, no action buttons
  if (currentPhase === 'physical') {
    return (
      <div className="w-full">
        <PhysicalStopwatch phaseName={phaseName} onNextPhase={onNextPhase} />

        {/* Relatie Notities Toggle Button */}
        {onToggleAiCard && (
          <button
            onClick={onToggleAiCard}
            className="w-full mt-1.5 py-1.5 px-2 rounded-lg text-xs font-medium transition-colors flex items-center justify-center gap-1.5 bg-gray-50 hover:bg-gray-100 text-gray-600 border border-gray-200"
          >
            <RelationIcon size={14} weight="regular" className="text-gray-600" />
            <span>Relatie Notities</span>
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="w-full">
      {/* Row: Action buttons grid (links) + Timer (rechts) */}
      <div className="flex gap-2 mb-1.5">
        {/* Links: 2x2 Grid met actie knoppen */}
        <div className="grid grid-cols-2 gap-1.5 flex-1">
          {/* Afbreken Button */}
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onBreakConversation();
            }}
            className="w-full bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 py-1.5 px-2 rounded-lg text-xs font-medium transition-colors flex items-center justify-start gap-1.5"
          >
            <span className="text-sm">△</span>
            <span>Afbreken</span>
          </button>

          {/* Volgende Button — disabled while a phase transition is in flight */}
          <button
            data-testid="next-step"
            onClick={onNextPhase}
            disabled={isTransitionPending}
            aria-busy={isTransitionPending}
            className={`w-full py-1.5 px-2 rounded-lg text-xs font-medium transition-colors flex items-center justify-start gap-1.5 shadow-sm ${
              isTransitionPending
                ? 'bg-orange-300 cursor-not-allowed opacity-70 text-white'
                : 'bg-orange-500 hover:bg-orange-600 text-white'
            }`}
          >
            <span className="text-sm">{isTransitionPending ? '⏳' : '▶'}</span>
            <span>{isTransitionPending ? 'Bezig...' : 'Volgende'}</span>
          </button>

          {/* Customizable Quick Action Button */}
          <button
            onClick={onQuickAction}
            className="w-full bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 py-1.5 px-2 rounded-lg text-xs font-medium transition-colors flex items-center justify-start gap-1.5"
            title={quickActionConfig.label}
          >
            <span className="text-sm">{quickActionConfig.icon}</span>
            <span>{quickActionConfig.label}</span>
          </button>

          {/* Plannen Button */}
          <button
            onClick={onScheduleAppointment}
            className="w-full bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 py-1.5 px-2 rounded-lg text-xs font-medium transition-colors flex items-center justify-start gap-1.5"
          >
            <span className="text-sm">📅</span>
            <span>Plannen</span>
          </button>
        </div>

        {/* Rechts: Timer indicator horizontaal */}
        <div className="w-48 md:w-64 flex-shrink-0 min-w-0">
          <div className="flex items-center justify-between bg-white border border-gray-200 rounded-lg p-1.5 min-w-0">
            <div className="flex items-center space-x-1 min-w-0 flex-1">
              <Clock className="w-3.5 h-3.5 text-orange-600 flex-shrink-0" />
              <span className="font-medium text-gray-700 text-xs whitespace-nowrap">{formatTime(timer)}</span>
              <span className="text-[10px] text-gray-500 truncate min-w-0">{phaseName}</span>
            </div>
            <button
              onClick={onPlusOneMinute}
              className="bg-orange-600 hover:bg-orange-700 text-white text-[10px] font-medium px-1.5 py-1 rounded transition-colors flex-shrink-0 whitespace-nowrap"
            >
              +1 min
            </button>
          </div>
        </div>
      </div>

      {/* Relatie Notities Toggle Button */}
      {onToggleAiCard && (
        <button
          onClick={onToggleAiCard}
          className="w-full py-1.5 px-2 rounded-lg text-xs font-medium transition-colors flex items-center justify-center gap-1.5 bg-gray-50 hover:bg-gray-100 text-gray-600 border border-gray-200"
        >
          <RelationIcon size={14} weight="regular" className="text-gray-600" />
          <span>Relatie Notities</span>
          <span className="text-[10px] text-gray-500">Inschakelen</span>
        </button>
      )}
    </div>
  );
}



